import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  alpha,
} from '@mui/material';
import { Business as BusinessIcon, LinkOff as LinkOffIcon } from '@mui/icons-material';
import clientService from 'src/clientService';
import { OrganisationResponse } from 'src/api/Api';
import { useSnackbar } from 'src/view/components/SnackbarService';

interface AssignProjectOrgModalProps {
  open: boolean;
  onClose: () => void;
  project?: {
    project_id: string;
    project_name: string;
    org_id?: string | null;
  } | null;
  defaultOrgId?: string;
  onAssigned?: () => void;
}

export const AssignProjectOrgModal: React.FC<AssignProjectOrgModalProps> = ({
  open,
  onClose,
  project,
  defaultOrgId,
  onAssigned,
}) => {
  const [organisations, setOrganisations] = useState<OrganisationResponse[]>([]);
  const [projectsList, setProjectsList] = useState<{ project_id: string; project_name: string }[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [selectedOrgId, setSelectedOrgId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const { showSnackbar, snackbarType } = useSnackbar();

  // Mode flag: when opened from Organisation Overview page (defaultOrgId provided, project is null)
  const isOrgContext = Boolean(defaultOrgId && !project);

  useEffect(() => {
    if (open) {
      setSelectedOrgId(project?.org_id || defaultOrgId || '');
      setSelectedProjectId(project?.project_id || '');
      
      if (!isOrgContext) {
        fetchOrganisations();
      }
      if (!project) {
        fetchProjects();
      }
    }
  }, [open, project, defaultOrgId, isOrgContext]);

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const response = await clientService.api.getAllProjectsAdminApiV1AdminProjectsGet();
      const list = Array.isArray(response.data) ? response.data : (response.data as any)?.data || [];
      
      // Filter projects to only show unassigned projects
      const unassignedProjects = list.filter((p: any) => !p.org_id);
      
      setProjectsList(
        unassignedProjects.map((p: any) => ({
          project_id: p.project_id,
          project_name: p.project_name || 'Unnamed Project',
        }))
      );
    } catch (err) {
      console.error('Failed to fetch projects list:', err);
      showSnackbar({
        message: 'Failed to load unassigned projects list.',
        type: snackbarType.ERROR,
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchOrganisations = async () => {
    setLoading(true);
    try {
      const response = await clientService.api.listOrganisationsApiV1OrganisationsGet({
        skip: 0,
        limit: 500,
      });
      setOrganisations(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch organisations:', error);
      showSnackbar({
        message: 'Failed to load organisations list.',
        type: snackbarType.ERROR,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (forceUnassign = false) => {
    const targetProjectId = project?.project_id || selectedProjectId;
    const isUnassigning = forceUnassign || selectedOrgId === 'unassign' || selectedOrgId === '';
    const targetOrgId = isUnassigning ? null : (selectedOrgId || defaultOrgId);

    if (!targetProjectId) {
      showSnackbar({
        message: 'Please select a project.',
        type: snackbarType.WARNING,
      });
      return;
    }

    if (!isUnassigning && !targetOrgId) {
      showSnackbar({
        message: 'Please select an organisation.',
        type: snackbarType.WARNING,
      });
      return;
    }

    setSaving(true);
    try {
      await clientService.api.updateProjectApiV1ProjectsProjectIdPut(targetProjectId, {
        org_id: targetOrgId,
      } as any);

      showSnackbar({
        message: targetOrgId
          ? 'Project assigned to organisation successfully.'
          : 'Project unassigned from organisation successfully.',
        type: snackbarType.SUCCESS,
      });

      if (onAssigned) {
        onAssigned();
      }
      onClose();
    } catch (error: any) {
      console.error('Failed to update project organisation assignment:', error);
      const msg = error?.response?.data?.detail || error?.message || 'Failed to update project organisation.';
      showSnackbar({
        message: typeof msg === 'string' ? msg : 'Failed to update project organisation.',
        type: snackbarType.ERROR,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: '#0D0D0D',
          color: '#fff',
          borderRadius: '16px',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          p: 1,
        },
      }}
    >
      <DialogTitle sx={{ pb: 1, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <BusinessIcon sx={{ color: '#10B981' }} />
        <Box>
          <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700 }}>
            {isOrgContext ? 'Assign Unassigned Project' : 'Assign Project to Organisation'}
          </Typography>
          {project && (
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.8rem' }}>
              Project: {project.project_name}
            </Typography>
          )}
        </Box>
      </DialogTitle>

      <DialogContent sx={{ py: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} sx={{ color: '#10B981' }} />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {/* Project Selection (shown when modal is opened from Organisation page) */}
            {!project && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 0.5 }}>
                  Select an unassigned project to assign to this organisation:
                </Typography>
                <FormControl fullWidth size="medium">
                  <InputLabel id="select-project-label" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                    Unassigned Project
                  </InputLabel>
                  <Select
                    labelId="select-project-label"
                    value={selectedProjectId}
                    label="Unassigned Project"
                    onChange={(e) => setSelectedProjectId(e.target.value)}
                    sx={{
                      color: '#fff',
                      bgcolor: 'rgba(255, 255, 255, 0.03)',
                      borderRadius: '10px',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.15)',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#10B981',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#10B981',
                      },
                      '& .MuiSvgIcon-root': {
                        color: '#fff',
                      },
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          bgcolor: '#151515',
                          color: '#fff',
                          borderRadius: '12px',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          maxHeight: 300,
                        },
                      },
                    }}
                  >
                    {projectsList.length === 0 ? (
                      <MenuItem disabled>
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.4)' }}>
                          No unassigned projects available
                        </Typography>
                      </MenuItem>
                    ) : (
                      projectsList.map((p) => (
                        <MenuItem
                          key={p.project_id}
                          value={p.project_id}
                          sx={{
                            '&:hover': { bgcolor: 'rgba(16, 185, 129, 0.15)' },
                            '&.Mui-selected': { bgcolor: alpha('#10B981', 0.25) },
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#fff' }}>
                            {p.project_name}
                          </Typography>
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Box>
            )}

            {/* Organisation Selection (shown ONLY when project is provided and not in organisation context) */}
            {!isOrgContext && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 0.5 }}>
                  Select the organisation that owns and manages this project:
                </Typography>
                <FormControl fullWidth size="medium">
                  <InputLabel id="select-org-label" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                    Organisation
                  </InputLabel>
                  <Select
                    labelId="select-org-label"
                    value={selectedOrgId}
                    label="Organisation"
                    onChange={(e) => setSelectedOrgId(e.target.value)}
                    sx={{
                      color: '#fff',
                      bgcolor: 'rgba(255, 255, 255, 0.03)',
                      borderRadius: '10px',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.15)',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#10B981',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#10B981',
                      },
                      '& .MuiSvgIcon-root': {
                        color: '#fff',
                      },
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          bgcolor: '#151515',
                          color: '#fff',
                          borderRadius: '12px',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          maxHeight: 300,
                        },
                      },
                    }}
                  >
                    <MenuItem
                      value="unassign"
                      sx={{
                        color: '#F87171',
                        '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.15)' },
                        '&.Mui-selected': { bgcolor: alpha('#EF4444', 0.25) },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LinkOffIcon sx={{ fontSize: 18 }} />
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          None (Unassign Project)
                        </Typography>
                      </Box>
                    </MenuItem>
                    {organisations.map((org) => (
                      <MenuItem
                        key={org.org_id}
                        value={org.org_id}
                        sx={{
                          '&:hover': { bgcolor: 'rgba(16, 185, 129, 0.15)' },
                          '&.Mui-selected': { bgcolor: alpha('#10B981', 0.25) },
                        }}
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#fff' }}>
                            {org.name}
                          </Typography>
                          {org.domain && (
                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.4)' }}>
                              {org.domain}
                            </Typography>
                          )}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1, justifyContent: 'space-between' }}>
        <Box>
          {project?.org_id && !isOrgContext && (
            <Button
              onClick={() => handleSave(true)}
              disabled={saving}
              color="error"
              startIcon={<LinkOffIcon />}
              sx={{
                color: '#EF4444',
                textTransform: 'none',
                fontWeight: 600,
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '8px',
                px: 2,
                '&:hover': {
                  bgcolor: 'rgba(239, 68, 68, 0.1)',
                  borderColor: '#EF4444',
                },
              }}
            >
              Unassign
            </Button>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            onClick={onClose}
            disabled={saving}
            sx={{
              color: 'rgba(255, 255, 255, 0.5)',
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => handleSave(false)}
            disabled={
              saving ||
              (isOrgContext && (!selectedProjectId || projectsList.length === 0)) ||
              (!isOrgContext && !selectedOrgId)
            }
            sx={{
              bgcolor: selectedOrgId === 'unassign' ? '#EF4444' : '#10B981',
              color: selectedOrgId === 'unassign' ? '#fff' : '#000',
              fontWeight: 700,
              textTransform: 'none',
              borderRadius: '10px',
              px: 3,
              '&:hover': {
                bgcolor: selectedOrgId === 'unassign' ? '#DC2626' : '#059669',
              },
            }}
          >
            {saving ? (
              <CircularProgress size={20} sx={{ color: selectedOrgId === 'unassign' ? '#fff' : '#000' }} />
            ) : selectedOrgId === 'unassign' ? (
              'Unassign Organisation'
            ) : (
              'Save Assignment'
            )}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
